import { useId, useMemo, useState } from 'react';
import { Lock, Trash2 } from 'lucide-react';
import type {
  MailAdminCandidateDto,
  MailAdminMemberDto,
  MailAdminMemberRole,
} from '../../../lib/mailAdminTypes';
import { filterCandidates, isLastOwner, memberLabel } from '../../../lib/mailAdminValidation';

/**
 * Controlled editor for a mailbox's members. A personal mailbox is read-only here: its owner
 * is fixed (shown locked) and delegation is not offered, so an admin cannot widen access to
 * one person's inbox from this screen. On a shared mailbox the last remaining owner can be
 * neither removed nor demoted.
 */
export function MailboxMembersEditor({
  members,
  candidates,
  personal,
  disabled,
  onChange,
}: {
  members: MailAdminMemberDto[];
  candidates: readonly MailAdminCandidateDto[];
  personal: boolean;
  disabled?: boolean;
  onChange: (members: MailAdminMemberDto[]) => void;
}) {
  const addId = useId();
  const [toAdd, setToAdd] = useState('');

  const addable = useMemo(
    () => filterCandidates(candidates, { excludeUids: members.map((m) => m.uid) }),
    [candidates, members],
  );

  const setRole = (uid: string, role: MailAdminMemberRole) =>
    onChange(members.map((m) => (m.uid === uid ? { ...m, role } : m)));
  const remove = (uid: string) => onChange(members.filter((m) => m.uid !== uid));
  const add = () => {
    const c = candidates.find((x) => x.uid === toAdd);
    if (!c) return;
    onChange([...members, { uid: c.uid, role: 'member', name: c.name, email: c.email }]);
    setToAdd('');
  };

  return (
    <div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {members.map((m) => {
          const lastOwner = !personal && isLastOwner(members, m.uid);
          const locked = personal && m.role === 'owner';
          const label = memberLabel(m);
          return (
            <li
              key={m.uid}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}
            >
              <span style={{ flex: '1 1 10rem', minWidth: 0, overflowWrap: 'anywhere' }}>
                {label}
                {m.email && m.name && <span className="cell-sub">{m.email}</span>}
              </span>
              {locked ? (
                <span className="label" title="The owner of a personal mailbox cannot be changed">
                  <Lock size={11} aria-hidden="true" /> Owner (locked)
                </span>
              ) : personal ? (
                <span className="label">{m.role === 'owner' ? 'Owner' : 'Member'}</span>
              ) : (
                <>
                  <select
                    aria-label={`Role for ${label}`}
                    value={m.role}
                    disabled={disabled || lastOwner}
                    title={lastOwner ? 'A mailbox needs at least one owner' : undefined}
                    onChange={(e) => setRole(m.uid, e.target.value as MailAdminMemberRole)}
                  >
                    <option value="owner">Owner</option>
                    <option value="member">Member</option>
                  </select>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    aria-label={`Remove ${label}`}
                    disabled={disabled || lastOwner}
                    title={lastOwner ? 'The last owner cannot be removed' : undefined}
                    onClick={() => remove(m.uid)}
                  >
                    <Trash2 size={13} aria-hidden="true" />
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>

      {personal ? (
        <div className="form-hint" style={{ marginTop: '0.6rem' }}>
          Members of a personal mailbox are fixed.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
          <label htmlFor={addId} style={{ position: 'absolute', left: -9999 }}>
            Add a member
          </label>
          <select
            id={addId}
            value={toAdd}
            disabled={disabled}
            onChange={(e) => setToAdd(e.target.value)}
            style={{ flex: '1 1 12rem', minWidth: 0 }}
          >
            <option value="">{addable.length ? 'Add a member…' : 'No more users to add'}</option>
            {addable.map((c) => (
              <option key={c.uid} value={c.uid}>
                {c.name} ({c.email})
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-secondary" disabled={disabled || !toAdd} onClick={add}>
            Add
          </button>
        </div>
      )}
    </div>
  );
}
